/**
 * SWR hooks for nat-booking-admin.
 *
 * Wraps api.admin.* calls with useSWR for automatic caching,
 * revalidation on focus, and deduplication.
 */

import useSWR, { type SWRConfiguration } from 'swr'
import { api } from './api'
import type {
  DashboardData, Booking, Service, Customer, User,
  ApiListResponse, UnifiedHomepage, WebsiteContent,
  HomepageContent, FaqItem, Testimonial, TeamMember, Department, Doctor,
  Room, RoomBooking, Package,
  BoardMember, ValueItem, Milestone, Certification, Partner,
  InsurancePartner, InsuranceStep,
  News,
  Branch,
  SvcCategory,
  JobPosting,
} from '@/types'

// ─── Helper: build SWR key with query params ────────────────────────────────

function listKey(base: string, params?: Record<string, string | undefined>) {
  if (!params) return base
  const clean = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${v}`)
    .join('&')
  return clean ? `${base}?${clean}` : base
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export function useDashboard(config?: SWRConfiguration) {
  return useSWR<DashboardData>('admin/dashboard', () => api.admin.dashboard(), {
    revalidateOnFocus: true,
    ...config,
  })
}

// ─── Bookings ────────────────────────────────────────────────────────────────

export function useBookings(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Booking>>(
    listKey('admin/bookings', params),
    () => api.admin.getBookings(params),
    config,
  )
}

export function useBooking(id: string | null, config?: SWRConfiguration) {
  return useSWR<Booking>(
    id ? `admin/bookings/${id}` : null,
    () => api.admin.getBooking(id!),
    config,
  )
}

// ─── Services ────────────────────────────────────────────────────────────────

export function useServices(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Service>>(
    listKey('admin/services', params),
    () => api.admin.getServices(params),
    config,
  )
}

// ─── Customers ───────────────────────────────────────────────────────────────

export function useCustomers(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Customer>>(
    listKey('admin/customers', params),
    () => api.admin.getCustomers(params),
    config,
  )
}

// ─── System Users ────────────────────────────────────────────────────────────

export function useSystemUsers(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<User>>(
    listKey('admin/system-users', params),
    () => api.admin.getSystemUsers(params),
    config,
  )
}

// ─── Departments ────────────────────────────────────────────────────────────

export function useDepartments(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: Department[] }>(
    'admin/departments',
    () => api.admin.getDepartments(),
    config,
  )
}

// ─── Doctors ────────────────────────────────────────────────────────────────

export function useDoctors(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Doctor>>(
    listKey('admin/doctors', params),
    () => api.admin.getDoctors(params),
    config,
  )
}

// ─── Rooms ──────────────────────────────────────────────────────────────────

export function useRooms(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Room>>(
    listKey('admin/rooms', params),
    () => api.admin.getRooms(params),
    config,
  )
}

// ─── Room Bookings ─────────────────────────────────────────────────────────

export function useRoomBookings(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<RoomBooking>>(
    listKey('admin/room-bookings', params),
    () => api.admin.getRoomBookings(params),
    config,
  )
}

// ─── Packages ───────────────────────────────────────────────────────────────

export function usePackages(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Package>>(
    listKey('admin/packages', params),
    () => api.admin.getPackages(params),
    config,
  )
}

// ─── News ───────────────────────────────────────────────────────────────────

export function useNews(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<News>>(
    listKey('admin/news', params),
    () => api.admin.getNews(params),
    config,
  )
}

// ─── Branches ───────────────────────────────────────────────────────────────

export function useBranches(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: Branch[] }>('admin/branches', () => api.admin.getBranches(), config)
}

// ─── Service Categories ─────────────────────────────────────────────────────

export function useSvcCategories(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<SvcCategory>>(
    listKey('admin/svc-categories', params),
    () => api.admin.getSvcCategories(params),
    config,
  )
}

// ─── About: Board / Values / Milestones / Certifications / Partners ────────

export function useBoard(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: BoardMember[] }>('admin/board', () => api.admin.getBoard(), config)
}
export function useValues(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: ValueItem[] }>('admin/values', () => api.admin.getValues(), config)
}
export function useMilestones(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: Milestone[] }>('admin/milestones', () => api.admin.getMilestones(), config)
}
export function useCertifications(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: Certification[] }>('admin/certifications', () => api.admin.getCertifications(), config)
}
export function usePartners(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: Partner[] }>('admin/partners', () => api.admin.getPartners(), config)
}
export function useInsurancePartners(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: InsurancePartner[] }>('admin/insurance-partners', () => api.admin.getInsurancePartners(), config)
}
export function useInsuranceSteps(config?: SWRConfiguration) {
  return useSWR<{ success: boolean; data: InsuranceStep[] }>('admin/insurance-steps', () => api.admin.getInsuranceSteps(), config)
}

// ─── Unified Homepage ────────────────────────────────────────────────────────

export function useHomepage(config?: SWRConfiguration) {
  return useSWR<UnifiedHomepage>('admin/homepage', () => api.admin.getHomepage(), {
    revalidateOnFocus: false,
    ...config,
  })
}

// ─── Homepage Content (banners/announcements/sections) ───────────────────────

export function useHomepageContents(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<HomepageContent>>(
    listKey('admin/homepage-content', params),
    () => api.admin.getHomepageContents(params),
    config,
  )
}

// ─── Website Content ─────────────────────────────────────────────────────────

export function useWebsiteContent(config?: SWRConfiguration) {
  return useSWR<WebsiteContent>('admin/website-content', () => api.admin.getWebsiteContent(), {
    revalidateOnFocus: false,
    ...config,
  })
}

// ─── FAQs ────────────────────────────────────────────────────────────────────

export function useFaqs(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<FaqItem>>(
    listKey('admin/faqs', params),
    () => api.admin.getFaqs(params),
    config,
  )
}

// ─── Job Postings ────────────────────────────────────────────────────────────

export function useJobPostings(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<JobPosting>>(
    listKey('admin/job-postings', params),
    () => api.admin.getJobPostings(params),
    config,
  )
}

// ─── Testimonials ────────────────────────────────────────────────────────────

export function useTestimonials(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<Testimonial>>(
    listKey('admin/testimonials', params),
    () => api.admin.getTestimonials(params),
    config,
  )
}

// ─── Team Members ────────────────────────────────────────────────────────────

export function useTeamMembers(params?: Record<string, string>, config?: SWRConfiguration) {
  return useSWR<ApiListResponse<TeamMember>>(
    listKey('admin/team', params),
    () => api.admin.getTeamMembers(params),
    config,
  )
}
